/**
 * NewsletterEditorPage — full-page editor for one newsletter:
 * Details → Audience → Content (TipTap + merge fields + templates) → Schedule
 * → Review/actions (test send, activate/pause, send now).
 *
 * Route: /crm/my-marketing/newsletters/:newsletterId
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  HStack,
  VStack,
  Icon,
  Button,
  IconButton,
  Input,
  Textarea,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Select as ChakraSelect,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  RadioGroup,
  Radio,
  Stack,
  Tag,
  Skeleton,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiSave,
  FiSend,
  FiMail,
  FiEye,
  FiClock,
  FiUsers,
  FiFileText,
  FiEdit3,
  FiPlay,
  FiPause,
  FiLayout,
  FiRefreshCw,
  FiAlertTriangle,
} from 'react-icons/fi';
import moment from 'moment';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsUsEast1 } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import {
  fetchNewsletter,
  updateNewsletter,
} from '../../../api/supabase/newsletters';
import {
  computeNextRunAt,
  upcomingOccurrences,
  describeSchedule,
  WEEKDAY_OPTIONS,
  FREQUENCY_OPTIONS,
  DEFAULT_TIMEZONE,
} from '../../../utils/newsletterSchedule';
import RichTextEmailEditor from '../../../components/CRM/Email/RichTextEmailEditor';
import DynamicVariableInsertButton from '../../../components/CRM/Email/DynamicVariableInsertButton';
import EmailPreviewModal from '../../../components/CRM/Email/EmailPreviewModal';
import AudienceBuilder from './components/AudienceBuilder';

function stripHtmlSnippet(html, maxLen = 200) {
  if (!html) return '';
  const text = html
    .replace(/<style[^>]*>.*?<\/style>/gis, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h >= 12 ? 'PM' : 'AM'}`,
}));
const MINUTE_OPTIONS = [0, 15, 30, 45];

function SectionCard({ icon, title, subtitle, children, ...rest }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const iconBg = useColorModeValue('blue.50', 'blue.900');

  return (
    <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" p={5} {...rest}>
      <HStack spacing={3} mb={4}>
        <Box bg={iconBg} p={2} borderRadius="md">
          <Icon as={icon} boxSize={4} color="blue.500" />
        </Box>
        <Box>
          <Heading size="sm">{title}</Heading>
          {subtitle && <Text fontSize="xs" color={mutedColor}>{subtitle}</Text>}
        </Box>
      </HStack>
      {children}
    </Box>
  );
}

function ScheduleEditor({ scheduleConfig, onChange }) {
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const previewBg = useColorModeValue('gray.50', 'gray.700');

  const sc = scheduleConfig || {};
  const set = (patch) => onChange({ timezone: DEFAULT_TIMEZONE, ...sc, ...patch });

  const occurrences = upcomingOccurrences(sc, 3);

  return (
    <VStack align="stretch" spacing={4}>
      <RadioGroup
        value={sc.mode || 'recurring'}
        onChange={(mode) => set({ mode })}
      >
        <Stack direction="row" spacing={6}>
          <Radio value="recurring" size="sm">Recurring</Radio>
          <Radio value="one_off" size="sm">One-time</Radio>
        </Stack>
      </RadioGroup>

      {sc.mode === 'one_off' ? (
        <FormControl maxW="320px">
          <FormLabel fontSize="sm">Send date & time</FormLabel>
          <Input
            type="datetime-local"
            size="sm"
            value={sc.sendAt ? moment(sc.sendAt).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={(e) => set({ sendAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
          />
          <FormHelperText fontSize="xs">Shown in your local time.</FormHelperText>
        </FormControl>
      ) : (
        <Flex gap={4} flexWrap="wrap">
          <FormControl w="170px">
            <FormLabel fontSize="sm">Frequency</FormLabel>
            <ChakraSelect
              size="sm"
              value={sc.frequency || 'monthly'}
              onChange={(e) => set({ frequency: e.target.value })}
            >
              {FREQUENCY_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </ChakraSelect>
          </FormControl>

          {(sc.frequency === 'weekly' || sc.frequency === 'biweekly') && (
            <FormControl w="170px">
              <FormLabel fontSize="sm">Day of week</FormLabel>
              <ChakraSelect
                size="sm"
                value={Number.isInteger(sc.weekday) ? sc.weekday : 2}
                onChange={(e) => set({ weekday: parseInt(e.target.value, 10) })}
              >
                {WEEKDAY_OPTIONS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </ChakraSelect>
            </FormControl>
          )}

          {(!sc.frequency || sc.frequency === 'monthly') && (
            <FormControl w="140px">
              <FormLabel fontSize="sm">Day of month</FormLabel>
              <NumberInput
                size="sm"
                min={1}
                max={28}
                value={Number.isInteger(sc.dayOfMonth) ? sc.dayOfMonth : 1}
                onChange={(_, num) => set({ dayOfMonth: Number.isNaN(num) ? 1 : num })}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          )}

          <FormControl w="150px">
            <FormLabel fontSize="sm">Time (ET)</FormLabel>
            <ChakraSelect
              size="sm"
              value={Number.isInteger(sc.hour) ? sc.hour : 9}
              onChange={(e) => set({ hour: parseInt(e.target.value, 10) })}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </ChakraSelect>
          </FormControl>

          <FormControl w="110px">
            <FormLabel fontSize="sm">Minute</FormLabel>
            <ChakraSelect
              size="sm"
              value={Number.isInteger(sc.minute) ? sc.minute : 0}
              onChange={(e) => set({ minute: parseInt(e.target.value, 10) })}
            >
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>:{String(m).padStart(2, '0')}</option>
              ))}
            </ChakraSelect>
          </FormControl>
        </Flex>
      )}

      <Box bg={previewBg} borderRadius="lg" p={3}>
        <HStack spacing={2} mb={occurrences.length > 0 ? 2 : 0}>
          <Icon as={FiClock} boxSize={4} color="blue.400" />
          <Text fontSize="xs" fontWeight="semibold">{describeSchedule(sc)}</Text>
        </HStack>
        {occurrences.length > 0 ? (
          <VStack align="stretch" spacing={0.5} pl={6}>
            {occurrences.map((d, i) => (
              <Text key={i} fontSize="xs" color={mutedColor}>
                {i === 0 ? 'Next: ' : ''}{moment(d).format('ddd, MMM D, YYYY [at] h:mm A')} ({moment(d).fromNow()})
              </Text>
            ))}
          </VStack>
        ) : (
          <Text fontSize="xs" color={mutedColor} pl={6}>
            No upcoming sends on this schedule{sc.mode === 'one_off' ? ' — pick a future date' : ''}.
          </Text>
        )}
      </Box>
    </VStack>
  );
}

export default function NewsletterEditorPage() {
  const { newsletterId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const templateHoverBg = useColorModeValue('blue.50', 'blue.900');
  const adminBannerBg = useColorModeValue('purple.50', 'purple.900');
  const adminBannerBorder = useColorModeValue('purple.200', 'purple.700');
  const adminBannerText = useColorModeValue('purple.800', 'purple.100');
  const snippetBg = useColorModeValue('gray.50', 'gray.700');
  const syncedCardBorder = useColorModeValue('blue.200', 'blue.600');
  const warnBg = useColorModeValue('orange.50', 'orange.900');
  const warnBorder = useColorModeValue('orange.200', 'orange.700');
  const warnText = useColorModeValue('orange.800', 'orange.100');

  const editorRef = useRef(null);
  const cancelRef = useRef();

  const [newsletter, setNewsletter] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [confirmSendNow, setConfirmSendNow] = useState(false);

  // Editable fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [audienceConfig, setAudienceConfig] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [sourceTemplateId, setSourceTemplateId] = useState(null);
  // 'custom' = own subject/body; 'template' = linked company template whose
  // LATEST version is fetched at every send (admins refresh it weekly/monthly).
  const [contentSource, setContentSource] = useState('custom');
  const [linkedTemplateId, setLinkedTemplateId] = useState(null);

  // Template gallery modal
  const templateModal = useDisclosure();
  const previewModal = useDisclosure();
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchNewsletter(newsletterId);
        if (cancelled) return;
        setNewsletter(data);
        setName(data.name || '');
        setDescription(data.description || '');
        setSubject(data.subject || '');
        setBodyHtml(data.bodyHtml || '');
        setReplyTo(data.replyTo || '');
        setAudienceConfig(data.audienceConfig);
        setScheduleConfig(data.scheduleConfig);
        setSourceTemplateId(data.sourceTemplateId || null);
        setContentSource(data.contentSource || 'custom');
        setLinkedTemplateId(data.linkedTemplateId || null);
      } catch (err) {
        console.error('[NewsletterEditor] Load failed:', err);
        toast({ title: 'Newsletter not found', status: 'error', duration: 4000 });
        history.push('/crm/my-marketing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [newsletterId, history, toast]);

  const markDirty = () => setDirty(true);

  // Identity for audience counts, samples, and sender merge fields is always
  // the newsletter's OWNER — so an admin editing another LO's newsletter sees
  // exactly what that LO would (their contacts, their sender.* fields).
  const ownerUid = newsletter?.ownerId || uid;
  const isEditingOtherUsers = Boolean(newsletter?.ownerId && uid && newsletter.ownerId !== uid);

  useEffect(() => {
    if (!isEditingOtherUsers) {
      setOwnerProfile(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'users', newsletter.ownerId))
      .then((snap) => {
        if (!cancelled && snap.exists()) setOwnerProfile(snap.data());
      })
      .catch((err) => console.error('[NewsletterEditor] Owner profile load failed:', err));
    return () => { cancelled = true; };
  }, [isEditingOtherUsers, newsletter?.ownerId]);

  const loadTemplates = useCallback(async () => {
    try {
      const snapshot = await getDocs(
        query(collection(db, 'emailTemplates'), where('category', '==', 'newsletter'))
      );
      setTemplates(
        snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Deactivated templates are unpublished: hidden here, and the
          // backend skips their sends (resolveNewsletterContent).
          .filter((t) => t.isActive !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } catch (err) {
      console.error('[NewsletterEditor] Template load failed:', err);
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    if ((templateModal.isOpen || contentSource === 'template') && templates === null) loadTemplates();
  }, [templateModal.isOpen, contentSource, templates, loadTemplates]);

  const linkedTemplate =
    contentSource === 'template' && templates
      ? templates.find((t) => t.id === linkedTemplateId) || null
      : null;

  // What this newsletter would send right now — template mode uses the linked
  // template's current content (matches backend resolveNewsletterContent).
  const effectiveSubject = contentSource === 'template' ? (linkedTemplate?.subject || '') : subject;
  const effectiveBody = contentSource === 'template' ? (linkedTemplate?.body || '') : bodyHtml;
  const hasSendableContent = contentSource === 'template'
    ? Boolean(linkedTemplateId)
    : Boolean(subject.trim() && bodyHtml.trim());

  const buildUpdates = useCallback((statusOverride) => {
    const status = statusOverride || newsletter?.status || 'draft';
    const updates = {
      name: name.trim() || 'Untitled Newsletter',
      description: description.trim() || null,
      subject: subject.trim() || null,
      bodyHtml: bodyHtml || null,
      replyTo: replyTo.trim() || null,
      contentSource,
      linkedTemplateId: contentSource === 'template' ? linkedTemplateId : null,
      audienceConfig,
      scheduleConfig,
      sourceTemplateId,
      status,
    };
    // Keep next_run_at consistent with the schedule whenever active.
    if (status === 'active') {
      const nextRun = computeNextRunAt(scheduleConfig);
      updates.nextRunAt = nextRun ? nextRun.toISOString() : null;
    } else {
      updates.nextRunAt = null;
    }
    return updates;
  }, [newsletter?.status, name, description, subject, bodyHtml, replyTo, contentSource, linkedTemplateId, audienceConfig, scheduleConfig, sourceTemplateId]);

  const handleSave = async (statusOverride) => {
    setSaving(true);
    try {
      const updated = await updateNewsletter(newsletterId, buildUpdates(statusOverride));
      setNewsletter(updated);
      setDirty(false);
      return updated;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = async () => {
    try {
      await handleSave();
      toast({ title: 'Newsletter saved', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleActivateToggle = async () => {
    const activating = newsletter?.status !== 'active';
    if (activating) {
      if (!hasSendableContent) {
        toast({
          title: contentSource === 'template' ? 'Pick a template first' : 'Add content first',
          description: contentSource === 'template'
            ? 'Choose which company template this newsletter stays synced to.'
            : 'A subject and body are required before activating.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      const nextRun = computeNextRunAt(scheduleConfig);
      if (!nextRun) {
        toast({
          title: 'Set a schedule first',
          description: 'This schedule has no upcoming send.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    }
    try {
      const updated = await handleSave(activating ? 'active' : 'paused');
      toast({
        title: activating ? 'Newsletter activated' : 'Newsletter paused',
        description: activating && updated.nextRunAt
          ? `Next send ${moment(updated.nextRunAt).format('MMM D [at] h:mm A')}`
          : undefined,
        status: activating ? 'success' : 'info',
        duration: 3500,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleTestSend = async () => {
    if (contentSource === 'template') {
      if (!linkedTemplate) {
        toast({
          title: linkedTemplateId ? 'Template still loading or missing' : 'Pick a template first',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
    } else if (!subject.trim() || !bodyHtml.trim()) {
      toast({ title: 'Add a subject and body first', status: 'warning', duration: 3000 });
      return;
    }
    setTestSending(true);
    try {
      const testSend = httpsCallable(functionsUsEast1, 'testSendNewsletter');
      const { data: result } = await testSend({
        subject: effectiveSubject,
        bodyHtml: effectiveBody,
        audienceConfig,
        ownerId: ownerUid,
      });
      toast({
        title: 'Test sent to your inbox',
        description: `Check ${result.sentTo}${result.sampleContactName ? ` — merged as if sent to ${result.sampleContactName}` : ''}`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Test send failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setTestSending(false);
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      await handleSave(); // send exactly what's on screen
      const sendNow = httpsCallable(functionsUsEast1, 'sendNewsletterNow');
      const { data: result } = await sendNow({ newsletterId });
      const mins = Math.max(1, Math.ceil((result.queuedCount || 0) / 25));
      toast({
        title: 'Newsletter is on its way',
        description: `${result.queuedCount} emails queued — delivers over ~${mins} minute${mins === 1 ? '' : 's'}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      setConfirmSendNow(false);
    } catch (err) {
      toast({ title: 'Send failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSendingNow(false);
    }
  };

  const applyTemplate = (template) => {
    setSubject(template.subject || '');
    setBodyHtml(template.body || '');
    setSourceTemplateId(template.id);
    markDirty();
    templateModal.onClose();
    toast({ title: `Template "${template.name}" applied`, status: 'success', duration: 2500 });
  };

  if (loading) {
    return (
      <Center minH="60vh" bg={pageBg}>
        <Spinner size="lg" color="blue.400" />
      </Center>
    );
  }

  const isActive = newsletter?.status === 'active';
  const statusColor = { draft: 'gray', active: 'green', paused: 'yellow', archived: 'red' }[newsletter?.status] || 'gray';

  // Primary actions — rendered in the header AND again above the Send-now
  // section, so after editing content/schedule at the bottom of a long page
  // the save/activate actions are right there (and "Send now" stops looking
  // like the page's save button). Plain render helper (not a nested
  // component) so Chakra buttons aren't remounted on every keystroke.
  const renderActionButtons = (extraProps = {}) => (
    <HStack spacing={2} {...extraProps}>
      <Button size="sm" variant="outline" leftIcon={<FiEye />} onClick={previewModal.onOpen}>
        Preview
      </Button>
      <Button
        size="sm"
        variant="outline"
        leftIcon={<FiMail />}
        onClick={handleTestSend}
        isLoading={testSending}
        loadingText="Sending"
      >
        Send me a test
      </Button>
      <Button
        size="sm"
        variant="outline"
        colorScheme={isActive ? 'yellow' : 'green'}
        leftIcon={isActive ? <FiPause /> : <FiPlay />}
        onClick={handleActivateToggle}
        isLoading={saving}
      >
        {isActive ? 'Pause' : 'Activate'}
      </Button>
      <Button size="sm" colorScheme="blue" leftIcon={<FiSave />} onClick={handleSaveClick} isLoading={saving}>
        Save
      </Button>
    </HStack>
  );

  return (
    <Box minH="100vh" bg={pageBg} pb={16}>
      <Container maxW="1100px" pt={6}>
        {/* Header */}
        <Box
          data-tour="nled-header"
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="sm"
          px={{ base: 4, md: 6 }}
          py={4}
          mb={5}
        >
          <Flex direction={{ base: 'column', md: 'row' }} gap={3} align={{ md: 'center' }} justify="space-between">
            <HStack spacing={3} flex={1} minW={0}>
              <IconButton
                icon={<FiArrowLeft />}
                variant="ghost"
                aria-label="Back to My Marketing"
                onClick={() => history.push('/crm/my-marketing')}
              />
              <Box flex={1} minW={0}>
                <Input
                  variant="flushed"
                  fontWeight="bold"
                  fontSize="lg"
                  value={name}
                  placeholder="Newsletter name"
                  onChange={(e) => { setName(e.target.value); markDirty(); }}
                />
                <HStack mt={1} spacing={2}>
                  <Badge colorScheme={statusColor} variant="subtle" textTransform="capitalize">
                    {newsletter?.status}
                  </Badge>
                  {isActive && newsletter?.nextRunAt && (
                    <Text fontSize="xs" color={mutedColor}>
                      Next send {moment(newsletter.nextRunAt).format('MMM D [at] h:mm A')}
                    </Text>
                  )}
                  {dirty && <Badge colorScheme="orange" variant="outline">Unsaved changes</Badge>}
                </HStack>
              </Box>
            </HStack>
            {renderActionButtons({ flexShrink: 0, 'data-tour': 'nled-actions' })}
          </Flex>

          {isEditingOtherUsers && (
            <Box mt={3} p={3} bg={adminBannerBg} borderRadius="md" borderWidth="1px" borderColor={adminBannerBorder}>
              <HStack spacing={2} align="flex-start">
                <Badge colorScheme="purple" mt={0.5} flexShrink={0}>Admin Mode</Badge>
                <Text fontSize="sm" color={adminBannerText}>
                  This newsletter belongs to{' '}
                  <Text as="span" fontWeight="bold">
                    {ownerProfile?.Name || ownerProfile?.name || ownerProfile?.email || 'another user'}
                  </Text>
                  . Audience counts use their contacts, and sends go out from their Gmail. Test
                  sends still arrive in your own inbox.
                </Text>
              </HStack>
            </Box>
          )}
        </Box>

        <VStack align="stretch" spacing={5}>
          {/* Details */}
          <SectionCard icon={FiEdit3} title="Details" subtitle="Internal name and reply handling" data-tour="nled-details">
            <Flex gap={4} flexWrap="wrap">
              <FormControl flex="1" minW="260px">
                <FormLabel fontSize="sm">Description (internal)</FormLabel>
                <Textarea
                  size="sm"
                  rows={2}
                  value={description}
                  placeholder="e.g. Monthly market update for my buyer's agents"
                  onChange={(e) => { setDescription(e.target.value); markDirty(); }}
                />
              </FormControl>
              <FormControl w={{ base: '100%', md: '300px' }}>
                <FormLabel fontSize="sm">Reply-to (optional)</FormLabel>
                <Input
                  size="sm"
                  type="email"
                  value={replyTo}
                  placeholder="Defaults to your email"
                  onChange={(e) => { setReplyTo(e.target.value); markDirty(); }}
                />
                <FormHelperText fontSize="xs">Replies go to your Gmail inbox either way.</FormHelperText>
              </FormControl>
            </Flex>
          </SectionCard>

          {/* Audience */}
          <SectionCard icon={FiUsers} title="Audience" subtitle="Who receives this newsletter — resolved fresh at every send" data-tour="nled-audience">
            {audienceConfig !== null && (
              <AudienceBuilder
                uid={ownerUid}
                value={audienceConfig}
                onChange={(next) => { setAudienceConfig(next); markDirty(); }}
              />
            )}
          </SectionCard>

          {/* Content */}
          <SectionCard
            icon={FiFileText}
            title="Content"
            subtitle={contentSource === 'template'
              ? 'Synced to a company template — every send uses its latest version'
              : 'Personalize with merge fields — each realtor gets their own copy'}
            data-tour="nled-content"
          >
            <VStack align="stretch" spacing={4}>
              <RadioGroup
                value={contentSource}
                onChange={(next) => { setContentSource(next); markDirty(); }}
              >
                <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 2, md: 8 }}>
                  <Radio value="custom">
                    <Text fontSize="sm">Write my own content</Text>
                  </Radio>
                  <Radio value="template">
                    <HStack spacing={2}>
                      <Text fontSize="sm">Use a company template</Text>
                      <Tag size="sm" colorScheme="blue" variant="subtle">
                        <Icon as={FiRefreshCw} boxSize={2.5} mr={1} />
                        auto-updates
                      </Tag>
                    </HStack>
                  </Radio>
                </Stack>
              </RadioGroup>

              {contentSource === 'template' ? (
                <VStack align="stretch" spacing={3}>
                  <FormControl maxW="440px">
                    <FormLabel fontSize="sm">Company template</FormLabel>
                    {templates === null ? (
                      <Skeleton h="32px" borderRadius="md" />
                    ) : (
                      <ChakraSelect
                        size="sm"
                        placeholder="Choose a template…"
                        value={linkedTemplateId || ''}
                        onChange={(e) => { setLinkedTemplateId(e.target.value || null); markDirty(); }}
                      >
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name || 'Untitled template'}</option>
                        ))}
                      </ChakraSelect>
                    )}
                    <FormHelperText fontSize="xs">
                      Admins keep these templates fresh (e.g. a weekly realtor newsletter updated
                      every Sunday). Each send automatically uses whatever the template contains at
                      that moment — merge fields still personalize every copy. If the template
                      hasn&apos;t changed since your last issue, the scheduled send skips (no
                      duplicates) and tries again next cycle.
                    </FormHelperText>
                  </FormControl>

                  {templates !== null && templates.length === 0 && (
                    <Box p={3} bg={warnBg} borderWidth="1px" borderColor={warnBorder} borderRadius="md">
                      <HStack spacing={2}>
                        <Icon as={FiAlertTriangle} color={warnText} />
                        <Text fontSize="sm" color={warnText}>
                          No company newsletter templates exist yet — ask a CRM admin to publish one
                          under CRM Admin → Templates (category "newsletter").
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {linkedTemplateId && templates !== null && !linkedTemplate && templates.length > 0 && (
                    <Box p={3} bg={warnBg} borderWidth="1px" borderColor={warnBorder} borderRadius="md">
                      <HStack spacing={2}>
                        <Icon as={FiAlertTriangle} color={warnText} />
                        <Text fontSize="sm" color={warnText}>
                          The linked template no longer exists or was deactivated by an admin.
                          Scheduled sends are skipped until it returns or you pick another template.
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {newsletter?.lastSkippedAt && (
                    <Box p={3} bg={warnBg} borderWidth="1px" borderColor={warnBorder} borderRadius="md">
                      <HStack spacing={2} align="flex-start">
                        <Icon as={FiAlertTriangle} color={warnText} mt={0.5} />
                        <Text fontSize="sm" color={warnText}>
                          The {moment(newsletter.lastSkippedAt).format('MMM D')} scheduled send was
                          skipped —{' '}
                          {newsletter.lastSkipReason === 'missing_template_content'
                            ? 'the linked template was missing, empty, or deactivated.'
                            : 'the template hadn\u2019t been updated since your last issue, so your realtors weren\u2019t sent duplicate content.'}
                          {' '}It sends automatically next cycle once the template changes.
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {linkedTemplate && (
                    <Box borderWidth="1px" borderColor={syncedCardBorder} borderRadius="lg" p={4}>
                      <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                        <Box minW={0}>
                          <HStack spacing={2} mb={1}>
                            <Tag size="sm" colorScheme="blue">
                              <Icon as={FiRefreshCw} boxSize={2.5} mr={1} />
                              Synced
                            </Tag>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                              {linkedTemplate.name || 'Untitled template'}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                            Current subject: {linkedTemplate.subject || '(no subject)'}
                          </Text>
                        </Box>
                        <Button size="xs" variant="outline" leftIcon={<FiEye />} onClick={previewModal.onOpen}>
                          Preview current version
                        </Button>
                      </Flex>
                      <Box bg={snippetBg} borderRadius="md" p={3} mt={3}>
                        <Text fontSize="xs" color={mutedColor} noOfLines={3}>
                          {stripHtmlSnippet(linkedTemplate.body) || 'No content yet'}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </VStack>
              ) : (
                <>
                  <Flex gap={3} align="flex-end" flexWrap="wrap">
                    <FormControl flex={1} minW="260px">
                      <FormLabel fontSize="sm">Subject</FormLabel>
                      <Input
                        size="sm"
                        value={subject}
                        placeholder={'e.g. {{contact.firstName}}, your market update for August'}
                        onChange={(e) => { setSubject(e.target.value); markDirty(); }}
                      />
                    </FormControl>
                    <Button size="sm" variant="outline" leftIcon={<FiLayout />} onClick={templateModal.onOpen}>
                      Start from template
                    </Button>
                  </Flex>
                  <Box>
                    <RichTextEmailEditor
                      ref={editorRef}
                      value={bodyHtml}
                      onChange={(html) => { setBodyHtml(html); markDirty(); }}
                      placeholder={'Hi {{contact.firstName}}, here\u2019s what\u2019s happening in the market this month...'}
                      minHeight="320px"
                      toolbarExtra={<DynamicVariableInsertButton editor={editorRef.current} />}
                    />
                  </Box>
                </>
              )}

              <Text fontSize="xs" color={mutedColor}>
                Your Gmail signature and a compliant unsubscribe footer are added automatically on send.
              </Text>
            </VStack>
          </SectionCard>

          {/* Schedule */}
          <SectionCard icon={FiClock} title="Schedule" subtitle="When this newsletter goes out (sends from your Gmail)" data-tour="nled-schedule">
            {scheduleConfig !== null && (
              <ScheduleEditor
                scheduleConfig={scheduleConfig}
                onChange={(next) => { setScheduleConfig(next); markDirty(); }}
              />
            )}
          </SectionCard>

          {/* Primary actions repeated after the last section — saving or
              activating is the normal way to finish editing (Send now below
              is only for immediate one-off blasts). */}
          <Flex justify="flex-end">
            {renderActionButtons()}
          </Flex>

          {/* Send now */}
          <Box
            data-tour="nled-send-now"
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            shadow="sm"
            p={5}
          >
            <Flex justify="space-between" align="center" gap={4} flexWrap="wrap">
              <Box>
                <Heading size="sm">Send a one-off blast now</Heading>
                <Text fontSize="xs" color={mutedColor}>
                  Saves your changes, then sends this issue immediately — the recurring schedule is not affected.
                </Text>
              </Box>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                leftIcon={<FiSend />}
                onClick={() => setConfirmSendNow(true)}
                isDisabled={!hasSendableContent}
              >
                Send now
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      {/* Template gallery modal */}
      <Modal isOpen={templateModal.isOpen} onClose={templateModal.onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Company newsletter templates</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {templates === null ? (
              <VStack align="stretch" spacing={3}>
                <Skeleton h="70px" borderRadius="md" /><Skeleton h="70px" borderRadius="md" />
              </VStack>
            ) : templates.length === 0 ? (
              <Text fontSize="sm" color={mutedColor} py={6} textAlign="center">
                No newsletter templates yet — ask a CRM admin to add some under Admin → Templates
                (category "newsletter").
              </Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {templates.map((t) => (
                  <Box
                    key={t.id}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: templateHoverBg, borderColor: 'blue.300' }}
                    onClick={() => applyTemplate(t)}
                  >
                    <Flex justify="space-between" align="center" gap={3}>
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{t.name || 'Untitled template'}</Text>
                        <Text fontSize="xs" color={mutedColor} noOfLines={1}>{t.subject || '(no subject)'}</Text>
                      </Box>
                      <Tag size="sm" colorScheme="blue" flexShrink={0}>Use template</Tag>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter pt={0}>
            <Text fontSize="xs" color={mutedColor}>
              Applying a template replaces the current subject and body.
            </Text>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview modal — template mode previews the template's current version */}
      <EmailPreviewModal
        isOpen={previewModal.isOpen}
        onClose={previewModal.onClose}
        body={effectiveBody}
        subject={effectiveSubject || '(no subject)'}
        fromEmail={currentUser?.email || ''}
        toLabel="to your realtors"
      />

      {/* Send-now confirmation */}
      <AlertDialog
        isOpen={confirmSendNow}
        leastDestructiveRef={cancelRef}
        onClose={() => !sendingNow && setConfirmSendNow(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg">Send "{name}" now?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Your latest edits are saved first, then the newsletter sends to your current audience
              from your Gmail at ~25 emails/minute. Unsubscribed and Do-Not-Contact realtors are
              excluded automatically.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setConfirmSendNow(false)} isDisabled={sendingNow}>
                Cancel
              </Button>
              <Button colorScheme="blue" leftIcon={<FiSend />} onClick={handleSendNow} ml={3} isLoading={sendingNow}>
                Send now
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
